import { Component, computed, inject, input, linkedSignal, output, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { RolesClient, UserRoleDetailedDto, UserRoleMiniDto, UserRolesClient, UserRoleUpdateDto } from '@unity/clients';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';
import { DetailFormService } from '@unity/services/detail-form.service';
import { DialogSharedComponent } from '@unity/shared/presentation/dialog-shared/dialog-shared.component';
import { nonZeroFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema } from '@unity/utils/types';

class UserRoleUpdateSchema implements FormGroupControlSchema<UserRoleUpdateDto> {
  public user_id: FormControl<number>;
  public role_id: FormControl<number>;
  public agency_id: FormControl<number>;

  public constructor(dto?: UserRoleDetailedDto) {
    this.user_id = nonZeroFormControl({ disabled: true, value: dto?.user.id });
    this.agency_id = nonZeroFormControl({ disabled: true, value: dto?.agency.id });
    this.role_id = nonZeroFormControl({ value: dto?.id });
  }
}

@Component({
  selector: 'unity-dialog-edit-user-role-shared',
  imports: [KENDO_BUTTON, ReactiveFormsModule, DialogSharedComponent, DropdownControlComponent],
  templateUrl: './dialog-edit-user-role-shared.html',
  styleUrl: './dialog-edit-user-role-shared.scss',
})
export class DialogEditUserRoleShared {
  #rolesClient = inject(RolesClient);
  #userRolesClient = inject(UserRolesClient);
  #formBuilder = inject(FormBuilder);
  #detailFormService = inject(DetailFormService);

  private userRoleDetailedResource = rxResource({
    params: () => this.$userRole(),
    stream: ({ params: { agency_id, user_id, id } }) => this.#userRolesClient.getOneAsDetailed(agency_id, user_id, id),
  });

  private availableRolesResource = rxResource({
    defaultValue: [],
    params: () => this.$userRole(),
    stream: ({ params: { agency_id, user_id } }) => this.#rolesClient.getAllAvailableForAgencyUserAsMini(agency_id, user_id),
  });

  protected readonly $formGroup = computed(() =>
    this.userRoleDetailedResource.hasValue()
      ? this.#formBuilder.group<UserRoleUpdateSchema>(new UserRoleUpdateSchema(this.userRoleDetailedResource.value()))
      : undefined
  );
  protected $availableRoles = this.availableRolesResource.value.asReadonly();
  protected $userRoleDetailed = this.userRoleDetailedResource.value.asReadonly();
  protected readonly $selected = linkedSignal<UserRoleMiniDto>(() => this.$userRole());
  protected readonly $canSubmit = computed(() => {
    const selected = this.$selected();
    const formGroup = this.$formGroup();
    if (selected.id !== untracked(this.$userRole).id) {
      formGroup?.updateValueAndValidity();
      return formGroup?.valid;
    }
    return false;
  });

  public readonly $userRole = input.required<UserRoleMiniDto>({ alias: 'userRole' });

  public closeEvent = output<void>();
  public changeRoleEvent = output<{ userRole: UserRoleMiniDto; update: UserRoleUpdateDto }>();

  protected onSave() {
    const formGroup = this.$formGroup();
    if (!formGroup) {
      return;
    }
    formGroup.updateValueAndValidity();

    if (formGroup.valid) {
      const output = { userRole: this.$userRole(), update: formGroup.getRawValue() };
      this.changeRoleEvent.emit(output);
      this.closeEvent.emit();
    } else {
      this.#detailFormService.handleFormErrors(formGroup);
    }
  }

  protected onSelection = (selected: UserRoleMiniDto) => this.$selected.set(selected);
  protected onClear = () => this.$selected.set(this.$userRole());
  protected onClose = () => this.closeEvent.emit();
}
